import React from 'react'
import PropTypes from 'prop-types'
import { assembleScope } from './domainScope'
import './DomainScopePicker.css'

// Clicks the domain scope together from the violation URL instead of typing it.
// Selection lives in the domain name itself (`subDepth`/`pathDepth` are derived
// from it by the caller), so there is no state to keep in sync.
const DomainScopePicker = ({
	decomposition,
	subDepth,
	pathDepth,
	hasError,
	onChange,
}) => {
	const { anchor, subdomainLabels, pathSegments, baselinePathDepth } =
		decomposition

	const clamp = (value, low, high) => Math.max(low, Math.min(high, value))

	// A subdomain label at index i is included when it is among the last
	// `subDepth` labels — those adjacent to the anchor.
	const subIncluded = (i) => i >= subdomainLabels.length - subDepth
	const pathIncluded = (j) => j < pathDepth
	const pathFixed = (j) => j < baselinePathDepth

	const emit = (nextSubDepth, nextPathDepth) =>
		onChange(assembleScope(decomposition, nextSubDepth, nextPathDepth))

	// Included: drop it and everything left of it. Excluded: take it and
	// everything right of it — a scope is always a contiguous run.
	const clickSub = (i) => {
		const next = subIncluded(i)
			? subdomainLabels.length - i - 1
			: subdomainLabels.length - i
		emit(clamp(next, 0, subdomainLabels.length), pathDepth)
	}

	const clickPath = (j) => {
		if (pathFixed(j)) return
		const next = pathIncluded(j) ? j : j + 1
		emit(subDepth, clamp(next, baselinePathDepth, pathSegments.length))
	}

	const chipClass = (included, fixed) =>
		`domain-scope-chip ${fixed ? 'fixed' : included ? 'included' : 'excluded'}`

	return (
		<div className={`domain-scope-picker ${hasError ? 'error' : ''}`}>
			<div className='domain-scope-chips'>
				{subdomainLabels.map((label, i) => (
					<React.Fragment key={`sub-${i}`}>
						<button
							type='button'
							className={chipClass(subIncluded(i), false)}
							onClick={() => clickSub(i)}
							title={
								subIncluded(i)
									? `Remove ${label} from the scope`
									: `Include ${label} in the scope`
							}
						>
							{label}
						</button>
						<span className='domain-scope-separator'>.</span>
					</React.Fragment>
				))}
				<span className='domain-scope-chip anchor'>{anchor}</span>
				{pathSegments.map((segment, j) => (
					<React.Fragment key={`path-${j}`}>
						<span className='domain-scope-separator'>/</span>
						<button
							type='button'
							className={chipClass(pathIncluded(j), pathFixed(j))}
							disabled={pathFixed(j)}
							onClick={() => clickPath(j)}
							title={
								pathFixed(j)
									? 'Already covered by the current domain'
									: pathIncluded(j)
										? `Remove /${segment} from the scope`
										: `Include /${segment} in the scope`
							}
						>
							{segment}
						</button>
					</React.Fragment>
				))}
			</div>
			<div className='domain-scope-result'>
				Domain:
				<span className='domain-scope-result-value'>
					{assembleScope(decomposition, subDepth, pathDepth)}
				</span>
			</div>
		</div>
	)
}

DomainScopePicker.propTypes = {
	decomposition: PropTypes.shape({
		anchor: PropTypes.string.isRequired,
		subdomainLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
		pathSegments: PropTypes.arrayOf(PropTypes.string).isRequired,
		baselinePathDepth: PropTypes.number.isRequired,
	}).isRequired,
	subDepth: PropTypes.number.isRequired,
	pathDepth: PropTypes.number.isRequired,
	hasError: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
}

export default DomainScopePicker
